import { combineReducers } from 'redux'

// Action Types
export const UPDATE_OWNERSHIP_DATA = 'UPDATE_OWNERSHIP_DATA'
export const UPDATE_LOAN_DATA = 'UPDATE_LOAN_DATA'
export const UPDATE_RESIDENCE_DATA = 'UPDATE_RESIDENCE_DATA'
export const UPDATE_INCOME_DATA = 'UPDATE_INCOME_DATA'
export const UPDATE_CONSENT_DATA = 'UPDATE_CONSENT_DATA'
export const SET_CURRENT_STEP = 'SET_CURRENT_STEP'
export const SET_APPLICATION_IDS = 'SET_APPLICATION_IDS'
export const COMPLETE_APPLICATION = 'COMPLETE_APPLICATION'
export const RESET_REFI_APPLICATION = 'RESET_REFI_APPLICATION'

// LocalStorage key
const REFI_APP_STORAGE_KEY = 'refiApplication'

// LocalStorage helpers
export const loadRefiApplicationFromStorage = () => {
  try {
    const serializedState = localStorage.getItem(REFI_APP_STORAGE_KEY)
    if (serializedState === null) {
      return undefined
    }
    return JSON.parse(serializedState)
  } catch (err) {
    console.error('Error loading refi application from localStorage:', err)
    return undefined
  }
}

export const saveRefiApplicationToStorage = (state: any) => {
  try {
    const serializedState = JSON.stringify(state)
    localStorage.setItem(REFI_APP_STORAGE_KEY, serializedState)
  } catch (err) {
    console.error('Error saving refi application to localStorage:', err)
  }
}

export const clearRefiApplicationFromStorage = () => {
  try {
    localStorage.removeItem(REFI_APP_STORAGE_KEY)
  } catch (err) {
    console.error('Error clearing refi application from localStorage:', err)
  }
}

// Initial States
const initialOwnershipState = {
  ownershipStatus: '',
}

const initialLoanState = {
  monthlyPayment: '',
  currentLender: '',
  estimatedPayoff: '',
}

const initialResidenceState = {
  monthlyPayment: '',
  moveInMonth: '',
  moveInDay: '',
  moveInYear: '',
  ownershipStatus: 'rent',
}

const initialIncomeState = {
  currentEmployer: '',
  employmentType: '',
  annualIncome: '',
  startDateMonth: '',
  startDateDay: '',
  startDateYear: '',
}

const initialConsentState = {
  dobMonth: '',
  dobDay: '',
  dobYear: '',
  ssn: '',
  agreeToDisclosure: false,
}

const initialApplicationState = {
  productPackageId: null,
  organizationId: null,
  currentStep: 'ownership',
  isComplete: false,
}

// Individual Reducers
const ownershipReducer = (state = initialOwnershipState, action) => {
  switch (action.type) {
    case UPDATE_OWNERSHIP_DATA:
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const loanReducer = (state = initialLoanState, action) => {
  switch (action.type) {
    case UPDATE_LOAN_DATA:
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const residenceReducer = (state = initialResidenceState, action) => {
  switch (action.type) {
    case UPDATE_RESIDENCE_DATA:
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const incomeReducer = (state = initialIncomeState, action) => {
  switch (action.type) {
    case UPDATE_INCOME_DATA:
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const consentReducer = (state = initialConsentState, action) => {
  switch (action.type) {
    case UPDATE_CONSENT_DATA:
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const applicationReducer = (state = initialApplicationState, action) => {
  switch (action.type) {
    case SET_CURRENT_STEP:
      return { ...state, currentStep: action.payload }
    case SET_APPLICATION_IDS:
      return {
        ...state,
        productPackageId: action.payload.productPackageId,
        organizationId: action.payload.organizationId,
      }
    case COMPLETE_APPLICATION:
      return { ...state, isComplete: true }
    default:
      return state
  }
}

const refiApplicationReducer = combineReducers({
  ownership: ownershipReducer,
  loan: loanReducer,
  residence: residenceReducer,
  income: incomeReducer,
  consent: consentReducer,
  application: applicationReducer,
})

/**
 * On reset of the refi application, clear all state to start fresh.
 */
export default function reducer(state: any, action: any) {
  // Load from localStorage on first initialization
  if (state === undefined) {
    const savedState = loadRefiApplicationFromStorage()
    if (savedState) {
      state = savedState
    }
  }

  // Handle reset action
  if (action.type === RESET_REFI_APPLICATION) {
    clearRefiApplicationFromStorage()
    return refiApplicationReducer(undefined, action)
  }

  // Get new state
  const newState = refiApplicationReducer(state, action)

  // Save to localStorage on every state update (except initial load)
  if (state !== undefined && action.type !== '@@INIT') {
    saveRefiApplicationToStorage(newState)
  }

  return newState
}

// SELECTORS

export const selectRefiApplication = (state) => state.refiApplication || {}

export const selectOwnershipData = (state) => state.refiApplication?.ownership || initialOwnershipState

export const selectLoanData = (state) => state.refiApplication?.loan || initialLoanState

export const selectResidenceData = (state) => state.refiApplication?.residence || initialResidenceState

export const selectIncomeData = (state) => state.refiApplication?.income || initialIncomeState

export const selectConsentData = (state) => state.refiApplication?.consent || initialConsentState

export const selectCurrentStep = (state) => state.refiApplication?.application?.currentStep || 'ownership'

export const selectApplicationIds = (state) => ({
  productPackageId: state.refiApplication?.application?.productPackageId,
  organizationId: state.refiApplication?.application?.organizationId,
})

export const selectIsApplicationComplete = (state) => state.refiApplication?.application?.isComplete || false

